import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ChevronLeft, ChevronRight, Home, Plane, Edit2, Trash2, MapPin, Clock, CalendarDays } from 'lucide-react';
import TeamWeekView from './TeamWeekView';

/**
 * Google-Calendar-parity view for game + practice schedules.
 * Views: Month / Week / Day. Week+Day render an hour time-grid with events at
 * their real kickoff times; Month is a 6-row day grid. Untimed rows (TBD) show
 * in an all-day strip. Click empty space → Add prefilled; drag an event → move
 * (reschedule date and/or time via the normal edit path); click an event → a
 * details popover with Edit/Delete + Pub W/G. "+N more" opens a day popover.
 * Keyboard: d/w/m switch views, t = today, n/p = next/prev.
 *
 * NON-COMPETITION rows render exactly like games (they are in `games`); they are
 * never counted anywhere (counting lives in the list header, not here). The
 * sport/season come from the parent; the `filter`/`teamFilter` props apply here
 * identically to the list.
 */

const HOUR_H = 44;           // px per hour row in the time grid
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDateStr = (s) => { const [y, m, dd] = (s || '').split('-').map(Number); return new Date(y, (m || 1) - 1, dd || 1); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -d.getDay());

// "6:00 PM" / "18:00" → minutes from midnight; TBD / 12:00 AM / blank → null.
const parseTimeToMin = (t) => {
  if (!t) return null;
  const s = String(t).trim();
  if (/tbd/i.test(s)) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = +m[1]; const min = +m[2]; const ap = m[3] ? m[3].toUpperCase() : null;
  if (ap) { if (h === 12) h = 0; if (ap === 'PM') h += 12; }
  if (h === 0 && min === 0) return null; // 12:00 AM is the TBD sentinel
  return h * 60 + min;
};
const minToHHMM = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;         // 24h (for the edit path)
const minToLabel = (m) => { let h = Math.floor(m / 60); const mm = m % 60; const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${pad(mm)} ${ap}`; };

export default function CalendarGridView({
  games = [], events = [], teamLabels = {}, filter = 'all', teamFilter = 'all',
  onDateClick, onEventClick, onReschedule, onEdit, onDelete, readOnly = false,
  // Week -> By team (docs/TEAM_TOURNAMENTS.md §4)
  teamColumns = [], tournaments = [], orgId, sport, season, onChanged, jump, opponents = [], onAddOpponent,
}) {
  const { isDark } = useTheme();
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('ol.schedule.calview') || 'month'; } catch { return 'month'; }
  });
  const [anchor, setAnchor] = useState(new Date());
  const [openEvtId, setOpenEvtId] = useState(null);
  const [dayPopover, setDayPopover] = useState(null);
  const dragRef = useRef(null);

  useEffect(() => { try { localStorage.setItem('ol.schedule.calview', view); } catch {} }, [view]);
  const [weekLayout, setWeekLayout] = useState(() => {
    try { return localStorage.getItem('ol.schedule.calweek') || 'day'; } catch { return 'day'; }
  });
  useEffect(() => { try { localStorage.setItem('ol.schedule.calweek', weekLayout); } catch {} }, [weekLayout]);
  // The List view's "Open in Calendar" on a tournament: Week · By team at its first day.
  useEffect(() => {
    if (!jump?.date) return;
    setAnchor(parseDateStr(jump.date)); setView('week'); setWeekLayout('team');
  }, [jump]);

  const goToday = useCallback(() => setAnchor(new Date()), []);
  const step = useCallback((dir) => {
    setAnchor((a) => view === 'month' ? new Date(a.getFullYear(), a.getMonth() + dir, 1)
      : view === 'week' ? addDays(a, 7 * dir) : addDays(a, dir));
  }, [view]);

  // Keyboard shortcuts (ignore while typing in a field).
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea' || e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      if (k === 'd') setView('day');
      else if (k === 'w') setView('week');
      else if (k === 'm') setView('month');
      else if (k === 't') goToday();
      else if (k === 'n') step(1);
      else if (k === 'p') step(-1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [goToday, step]);

  // Flatten games + practices into per-occurrence records, honouring filters.
  const occByDate = useMemo(() => {
    const map = {};
    const push = (dateStr, o) => { (map[dateStr] = map[dateStr] || []).push(o); };
    if (filter !== 'practice') {
      for (const g of games) {
        if (!g.date) continue;
        if (filter === 'home' && g.home_away !== 'home') continue;
        if (filter === 'away' && g.home_away !== 'away') continue;
        const teams = g.games || [];
        if (teamFilter !== 'all' && !teams.some((t) => t.team_slug === teamFilter)) continue;
        const show = teamFilter !== 'all' ? teams.filter((t) => t.team_slug === teamFilter) : teams;
        if (show.length === 0) {
          // Non-competition / opponent-only rows with no per-team time still render.
          push(g.date, {
            id: `${g.id}-x`, type: 'game', teamSlug: null, startMin: null,
            title: g.opponent || 'Event', homeAway: g.home_away, status: g.scheduling_status, source: g,
          });
          continue;
        }
        show.forEach((t, ti) => {
          const label = teamLabels[t.team_slug] || (t.team_slug || '').toUpperCase() || '?';
          push(g.date, {
            // Unique per (slot, team, occurrence): a slot with two same-level games
            // on one date (double-header) or a stale duplicate team entry must not
            // collide on the React key. (migration_log_0093)
            id: `${g.id}-${t.team_slug}-${ti}`, type: 'game', teamSlug: t.team_slug,
            startMin: parseTimeToMin(t.time),
            title: `${label} vs ${g.opponent || 'TBD'}`, homeAway: t.is_home === true ? 'home' : t.is_home === false ? 'away' : g.home_away,
            status: g.scheduling_status, source: g,
            // per-game facts for Week -> By team (one card = one team's game)
            gameId: t.game_id, date: g.date, opponent: t.opponent || g.opponent, opponentSlug: g.opponent_slug, venueSlug: t.venue_slug,
            venue: t.venue_name || t.venue_slug, round: t.round, slotState: t.slot_state, tournamentId: t.tournament_id, published: t.published,
          });
        });
      }
    }
    if (filter !== 'games' && filter !== 'home' && filter !== 'away') {
      for (const e of events) {
        if (!e.date) continue;
        push(e.date, {
          id: e.id, type: e.event_type || 'practice', teamSlug: e.team_slug || null,
          startMin: parseTimeToMin(e.start_time), title: e.title || 'Practice',
          status: e.scheduling_status, source: e,
        });
      }
    }
    for (const d in map) map[d].sort((a, b) => (a.startMin ?? 1e9) - (b.startMin ?? 1e9));
    return map;
  }, [games, events, teamLabels, filter, teamFilter]);

  const todayStr = toDateStr(new Date());

  // ---- shared styling helpers ----
  const pillColor = (o) => {
    if (o.type === 'game') {
      if (o.status === 'confirmed') return isDark ? 'bg-blue-700/70 text-blue-100 border-blue-500/40' : 'bg-blue-100 text-blue-800 border-blue-200';
      if (o.status === 'pending') return isDark ? 'bg-amber-700/70 text-amber-100 border-amber-500/40' : 'bg-amber-100 text-amber-900 border-amber-200';
      return isDark ? 'bg-slate-600/70 text-slate-100 border-slate-500/40' : 'bg-slate-200 text-slate-700 border-slate-300';
    }
    if (o.type === 'practice') return isDark ? 'bg-orange-700/70 text-orange-100 border-orange-500/40' : 'bg-orange-100 text-orange-900 border-orange-200';
    return isDark ? 'bg-purple-700/70 text-purple-100 border-purple-500/40' : 'bg-purple-100 text-purple-900 border-purple-200';
  };
  const dotColor = (o) => o.type !== 'game' ? (o.type === 'practice' ? 'bg-orange-500' : 'bg-purple-500')
    : (o.homeAway === 'home' ? 'bg-green-500' : 'bg-blue-500');

  // ---- drag to reschedule ----
  const onDragStart = (o) => (e) => { dragRef.current = o; try { e.dataTransfer.effectAllowed = 'move'; } catch {} };
  const dropOnDay = (dateStr) => (e) => {
    e.preventDefault();
    const o = dragRef.current; dragRef.current = null;
    if (!o || o.type !== 'game') return;
    if (o.source.date === dateStr) return;
    onReschedule?.(o.source, { date: dateStr });
  };
  const dropOnSlot = (dateStr, min) => (e) => {
    e.preventDefault();
    const o = dragRef.current; dragRef.current = null;
    if (!o || o.type !== 'game') return;
    const patch = {};
    if (o.source.date !== dateStr) patch.date = dateStr;
    if (o.teamSlug) patch.teamSlug = o.teamSlug, patch.time = minToHHMM(min);
    if (Object.keys(patch).length) onReschedule?.(o.source, patch);
  };
  const allowDrop = (e) => e.preventDefault();

  // ---- event details popover ----
  const EventPopover = ({ o, children }) => {
    const g = o.source;
    const wp = !!g.wp_pushed, gc = !!g.gcal_pushed;
    return (
      <Popover open={openEvtId === o.id} onOpenChange={(v) => setOpenEvtId(v ? o.id : null)}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent className="w-72" data-testid={`cal-evt-pop-${o.id}`}>
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{o.title}</div>
              {o.type === 'game' && (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {o.homeAway === 'home' ? <Home className="h-3 w-3 mr-1" /> : <Plane className="h-3 w-3 mr-1" />}
                  {o.homeAway === 'home' ? 'Home' : 'Away'}
                </Badge>
              )}
            </div>
            <div className={`text-xs flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              <Clock className="h-3 w-3" />{o.startMin != null ? minToLabel(o.startMin) : 'TBD'} · {parseDateStr(g.date).toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
            {g.location && (
              <div className={`text-xs flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <MapPin className="h-3 w-3" /><span className="truncate">{g.location}</span>
              </div>
            )}
            {o.type === 'game' && (
              <div className="flex items-center gap-2 text-xs">
                <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Status:</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${o.status === 'confirmed' ? 'bg-green-600 text-white' : 'bg-amber-500 text-white'}`}>
                  {o.status === 'confirmed' ? 'confirmed' : 'pending'}
                </span>
                <span className={`ml-auto flex items-center gap-1`} title="Pub state: W=WordPress, G=Google Calendar">
                  <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold ${wp ? 'bg-emerald-500 text-white' : (isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-300 text-slate-600')}`} data-testid={`cal-pub-wp-${o.id}`}>W</span>
                  <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold ${gc ? 'bg-emerald-500 text-white' : (isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-300 text-slate-600')}`} data-testid={`cal-pub-gc-${o.id}`}>G</span>
                </span>
              </div>
            )}
            {!readOnly && (
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" data-testid={`cal-evt-edit-${o.id}`}
                onClick={() => { setOpenEvtId(null); (onEdit || onEventClick)?.(g, o.type); }}>
                <Edit2 className="h-3 w-3 mr-1" />Edit
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 border-red-400/40" data-testid={`cal-evt-del-${o.id}`}
                onClick={() => { setOpenEvtId(null); onDelete?.(g, o.type); }}>
                <Trash2 className="h-3 w-3 mr-1" />Delete
              </Button>
            </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // ---- header (title + nav + view switch + mini-month) ----
  const title = view === 'month'
    ? anchor.toLocaleString('default', { month: 'long', year: 'numeric' })
    : view === 'week'
      ? (() => { const s = startOfWeek(anchor), e = addDays(s, 6); return `${s.toLocaleString('default', { month: 'short', day: 'numeric' })} – ${e.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`; })()
      : anchor.toLocaleString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const renderHeader = () => (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={() => step(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Popover>
          <PopoverTrigger asChild>
            <button className={`text-base font-semibold px-2 min-w-[200px] text-center rounded hover:${isDark ? 'bg-slate-700' : 'bg-slate-100'} ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="cal-title">
              {title}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64" data-testid="cal-minimonth">
            <MiniMonth value={anchor} onPick={(d) => setAnchor(d)} isDark={isDark} />
          </PopoverContent>
        </Popover>
        <Button size="sm" variant="ghost" onClick={() => step(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" onClick={goToday} className={`ml-1 text-xs h-7 ${isDark ? 'border-slate-600 text-slate-300' : ''}`} data-testid="cal-today">
          <CalendarDays className="h-3 w-3 mr-1" />Today
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <div className="hidden md:flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500" /><span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Home</span></span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /><span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Away</span></span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500" /><span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Practice</span></span>
        </div>
        <div className={`flex gap-0.5 p-0.5 rounded-md ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
          {['day', 'week', 'month'].map((v) => (
            <Button key={v} size="sm" variant={view === v ? 'default' : 'ghost'}
              className={`h-7 text-xs px-2 capitalize ${view === v ? 'bg-amber-600 text-white' : (isDark ? 'text-slate-300' : 'text-slate-600')}`}
              onClick={() => setView(v)} data-testid={`cal-view-${v}`}>{v}</Button>
          ))}
        </div>
        <div className={`flex gap-0.5 p-0.5 rounded-md ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`} title="Week layout">
          {[['day', 'By day'], ['team', 'By team']].map(([v, l]) => {
            const on = view === 'week' && weekLayout === v;
            return (
              <Button key={v} size="sm" variant={on ? 'default' : 'ghost'}
                className={`h-7 text-xs px-2 ${on ? 'bg-amber-600 text-white' : (isDark ? 'text-slate-300' : 'text-slate-600')}`}
                onClick={() => { setWeekLayout(v); setView('week'); }} data-testid={`cal-week-${v}`}>{l}</Button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ---- month view ----
  const renderMonth = () => {
    const y = anchor.getFullYear(), mo = anchor.getMonth();
    const first = new Date(y, mo, 1);
    const start = addDays(first, -first.getDay());
    const weeks = [];
    for (let w = 0; w < 6; w++) {
      const row = [];
      for (let d = 0; d < 7; d++) { const dt = addDays(start, w * 7 + d); row.push(dt); }
      weeks.push(row);
      if (w >= 4 && row.every((dt) => dt.getMonth() !== mo)) break;
    }
    const MAX = 3;
    return (
      <div className={`border rounded-lg overflow-hidden ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className="grid grid-cols-7">
          {DAY_NAMES.map((d) => (
            <div key={d} className={`text-center py-2 text-xs font-medium border-b ${isDark ? 'bg-slate-800 border-slate-700 text-slate-400' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>{d}</div>
          ))}
        </div>
        {weeks.map((row, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {row.map((dt) => {
              const ds = toDateStr(dt);
              const inMonth = dt.getMonth() === mo;
              const isToday = ds === todayStr;
              const evs = occByDate[ds] || [];
              const visible = evs.slice(0, MAX);
              const overflow = evs.length - MAX;
              return (
                <div key={ds} onDragOver={allowDrop} onDrop={dropOnDay(ds)}
                  className={`min-h-[112px] border-b border-r p-1 transition-colors
                    ${isDark ? 'border-slate-700' : 'border-slate-200'}
                    ${!inMonth ? (isDark ? 'bg-slate-900/40' : 'bg-slate-50/60') : (isDark ? 'bg-slate-800/20' : 'bg-white')}
                    ${isToday ? (isDark ? 'bg-amber-900/15' : 'bg-amber-50/70') : ''}`}
                  data-testid={`cal-day-${ds}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <button className={`text-xs font-medium leading-5 rounded hover:underline
                      ${isToday ? 'bg-amber-500 text-white w-5 h-5 flex items-center justify-center' : ''}
                      ${!inMonth ? (isDark ? 'text-slate-600' : 'text-slate-400') : (isDark ? 'text-slate-300' : 'text-slate-700')}`}
                      onClick={readOnly ? undefined : () => onDateClick?.(ds)} title={readOnly ? undefined : 'Add on this day'} data-testid={`cal-add-${ds}`}>
                      {dt.getDate() === 1 ? dt.toLocaleString('default', { month: 'short' }) + ' 1' : dt.getDate()}
                    </button>
                    {evs.length > 0 && <span className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{evs.length}</span>}
                  </div>
                  <div className="space-y-0.5">
                    {visible.map((o) => (
                      <EventPopover key={o.id} o={o}>
                        <div draggable={!readOnly && o.type === 'game'} onDragStart={onDragStart(o)}
                          className={`flex items-center gap-1 px-1 py-0.5 rounded border text-[10px] leading-snug cursor-pointer hover:opacity-90 ${pillColor(o)}`}
                          data-testid={`cal-event-${o.id}`}>
                          <span className={`shrink-0 h-1.5 w-1.5 rounded-full ${dotColor(o)}`} />
                          <span className="whitespace-normal break-words">{o.startMin != null ? minToLabel(o.startMin) + ' ' : ''}{o.title}</span>
                        </div>
                      </EventPopover>
                    ))}
                    {overflow > 0 && (
                      <Popover open={dayPopover === ds} onOpenChange={(v) => setDayPopover(v ? ds : null)}>
                        <PopoverTrigger asChild>
                          <button className={`text-[10px] font-medium px-1 py-0.5 rounded w-full text-left hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-testid={`cal-more-${ds}`}>+{overflow} more</button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64" data-testid={`cal-day-pop-${ds}`}>
                          <div className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {dt.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })}
                          </div>
                          <div className="space-y-1 max-h-64 overflow-auto">
                            {evs.map((o) => (
                              <EventPopover key={o.id} o={o}>
                                <div className={`flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer hover:opacity-90 ${pillColor(o)}`} data-testid={`cal-daypop-event-${o.id}`}>
                                  <span className={`shrink-0 h-1.5 w-1.5 rounded-full ${dotColor(o)}`} />
                                  <span>{o.startMin != null ? minToLabel(o.startMin) + ' ' : 'TBD '}{o.title}</span>
                                </div>
                              </EventPopover>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // ---- time-grid (week/day) ----
  const renderTimeGrid = (days) => {
    // Dynamic hour range from the events shown across the visible days.
    let minH = 17, maxH = 20;
    for (const dt of days) {
      for (const o of (occByDate[toDateStr(dt)] || [])) {
        if (o.startMin == null) continue;
        minH = Math.min(minH, Math.floor(o.startMin / 60));
        maxH = Math.max(maxH, Math.ceil((o.startMin + 60) / 60));
      }
    }
    minH = Math.max(6, Math.min(minH, 16));
    maxH = Math.min(23, Math.max(maxH, 21));
    const hours = []; for (let h = minH; h <= maxH; h++) hours.push(h);
    const gridH = hours.length * HOUR_H;

    return (
      <div className={`border rounded-lg overflow-hidden ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        {/* Day headers + all-day strip */}
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
          <div className={`border-b border-r ${isDark ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'}`} />
          {days.map((dt) => {
            const ds = toDateStr(dt); const isToday = ds === todayStr;
            return (
              <div key={ds} className={`text-center py-1.5 border-b border-r ${isDark ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-slate-50'}`}>
                <div className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{DAY_NAMES[dt.getDay()]}</div>
                <div className={`text-sm font-semibold inline-flex items-center justify-center rounded-full w-7 h-7 mx-auto ${isToday ? 'bg-amber-500 text-white' : (isDark ? 'text-slate-200' : 'text-slate-800')}`}>{dt.getDate()}</div>
              </div>
            );
          })}
        </div>
        {/* All-day / TBD row */}
        <div className="grid border-b" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)`, borderColor: isDark ? '#334155' : '#e2e8f0' }}>
          <div className={`text-[10px] px-1 py-1 text-right border-r ${isDark ? 'border-slate-700 text-slate-500' : 'border-slate-200 text-slate-400'}`}>all-day</div>
          {days.map((dt) => {
            const ds = toDateStr(dt);
            const tbd = (occByDate[ds] || []).filter((o) => o.startMin == null);
            return (
              <div key={ds} onDragOver={allowDrop} onDrop={dropOnDay(ds)}
                className={`min-h-[26px] p-0.5 border-r space-y-0.5 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                {tbd.map((o) => (
                  <EventPopover key={o.id} o={o}>
                    <div draggable={!readOnly && o.type === 'game'} onDragStart={onDragStart(o)}
                      className={`px-1 py-0.5 rounded border text-[10px] leading-snug cursor-pointer hover:opacity-90 ${pillColor(o)}`} data-testid={`cal-event-${o.id}`}>
                      <span className="whitespace-normal break-words">TBD · {o.title}</span>
                    </div>
                  </EventPopover>
                ))}
              </div>
            );
          })}
        </div>
        {/* Hour grid */}
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
          {/* hour labels */}
          <div className={`border-r ${isDark ? 'border-slate-700' : 'border-slate-200'}`} style={{ height: gridH }}>
            {hours.map((h) => (
              <div key={h} className={`text-[10px] text-right pr-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} style={{ height: HOUR_H }}>
                {minToLabel(h * 60)}
              </div>
            ))}
          </div>
          {days.map((dt) => {
            const ds = toDateStr(dt);
            const timed = (occByDate[ds] || []).filter((o) => o.startMin != null);
            return (
              <div key={ds} className={`relative border-r ${isDark ? 'border-slate-700' : 'border-slate-200'}`} style={{ height: gridH }}>
                {/* clickable hour cells */}
                {hours.map((h) => (
                  <div key={h} onClick={readOnly ? undefined : () => onDateClick?.(ds, minToHHMM(h * 60))}
                    onDragOver={allowDrop} onDrop={dropOnSlot(ds, h * 60)}
                    className={`border-b ${readOnly ? '' : 'cursor-pointer'} ${isDark ? 'border-slate-800 hover:bg-slate-700/30' : 'border-slate-100 hover:bg-blue-50/50'}`}
                    style={{ height: HOUR_H }} data-testid={`cal-slot-${ds}-${h}`} />
                ))}
                {/* events */}
                {timed.map((o) => {
                  const top = ((o.startMin - minH * 60) / 60) * HOUR_H;
                  return (
                    <div key={o.id} className="absolute left-0.5 right-0.5" style={{ top }}>
                      <EventPopover o={o}>
                        <div draggable={!readOnly && o.type === 'game'} onDragStart={onDragStart(o)}
                          className={`px-1.5 py-1 rounded border text-[11px] leading-snug cursor-pointer hover:opacity-90 shadow-sm ${pillColor(o)}`}
                          style={{ minHeight: HOUR_H - 6 }} data-testid={`cal-event-${o.id}`}>
                          <div className="font-semibold whitespace-normal break-words">{o.title}</div>
                          <div className="opacity-80">{minToLabel(o.startMin)}</div>
                        </div>
                      </EventPopover>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="calendar-grid-view">
      {renderHeader()}
      {view === 'month' && renderMonth()}
      {view === 'week' && weekLayout === 'day' && (
        <div className="overflow-auto max-h-[74vh]">{renderTimeGrid(Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)))}</div>
      )}
      {view === 'week' && weekLayout === 'team' && (
        <TeamWeekView days={Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i))} occByDate={occByDate}
          teamColumns={teamColumns} tournaments={tournaments} teamFilter={teamFilter} isDark={isDark} readOnly={readOnly}
          orgId={orgId} sport={sport} season={season} pillColor={pillColor} onChanged={onChanged} opponents={opponents} onAddOpponent={onAddOpponent} />
      )}
      {view === 'day' && (
        <div className="overflow-auto max-h-[74vh]">{renderTimeGrid([anchor])}</div>
      )}
    </div>
  );
}

// Small month picker used in the header title popover.
function MiniMonth({ value, onPick, isDark }) {
  const [cur, setCur] = useState(new Date(value.getFullYear(), value.getMonth(), 1));
  const y = cur.getFullYear(), mo = cur.getMonth();
  const first = new Date(y, mo, 1);
  const start = addDays(first, -first.getDay());
  const todayStr = toDateStr(new Date());
  const selStr = toDateStr(value);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <button className={`px-1 rounded hover:${isDark ? 'bg-slate-700' : 'bg-slate-100'}`} onClick={() => setCur(new Date(y, mo - 1, 1))}><ChevronLeft className="h-4 w-4" /></button>
        <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{cur.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <button className={`px-1 rounded hover:${isDark ? 'bg-slate-700' : 'bg-slate-100'}`} onClick={() => setCur(new Date(y, mo + 1, 1))}><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {DAY_NAMES.map((d) => <div key={d} className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{d[0]}</div>)}
        {cells.map((dt) => {
          const ds = toDateStr(dt);
          return (
            <button key={ds} onClick={() => onPick(dt)}
              className={`text-[11px] h-6 rounded ${dt.getMonth() !== mo ? (isDark ? 'text-slate-600' : 'text-slate-300') : (isDark ? 'text-slate-200' : 'text-slate-700')}
                ${ds === selStr ? 'bg-amber-600 text-white' : ds === todayStr ? (isDark ? 'ring-1 ring-amber-500' : 'ring-1 ring-amber-400') : `hover:${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}`}>
              {dt.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
